import type { StoryPublicPreludeBeat } from "@actale/contracts";

export interface StoryPreludeNarrativeContext {
  index?: number;
  previousBeat?: StoryPublicPreludeBeat | undefined;
}

interface GregorianMoment {
  year: number;
  month: number;
  day: number;
  hour: number | null;
  minute: number | null;
  dayValue: number;
  minuteValue: number | null;
}

function coordinateParts(value: string | undefined): string[] {
  return (value ?? "")
    .split(/\s*[·•]\s*/u)
    .map((part) => part.trim())
    .filter(Boolean);
}

function naturalTime(value: string | undefined): string {
  const parts = coordinateParts(value);
  if (parts.length < 2) return parts[0] ?? (value ?? "").trim();
  const [first, second] = parts as [string, string, ...string[]];
  if (/^\d{1,2}:\d{2}$/u.test(second)) return `${first} ${parts.slice(1).join(" ")}`;
  if (/[年月日]/u.test(first) && /^(凌晨|清晨|早晨|上午|中午|下午|傍晚|晚间|夜晚|深夜)$/u.test(second)) return parts.join("");
  return parts.join("，");
}

function parseGregorianMoment(value: string | undefined): GregorianMoment | null {
  const raw = (value ?? "").trim();
  const date = raw.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/u);
  if (!date) return null;
  const year = Number(date[1]);
  const month = Number(date[2]);
  const day = Number(date[3]);
  const clock = raw.match(/(\d{1,2}):(\d{2})/u);
  const hour = clock ? Number(clock[1]) : null;
  const minute = clock ? Number(clock[2]) : null;
  const dayValue = Date.UTC(year, month - 1, day) / 86_400_000;
  const dateCheck = new Date(Date.UTC(year, month - 1, day));
  if (
    dateCheck.getUTCFullYear() !== year ||
    dateCheck.getUTCMonth() !== month - 1 ||
    dateCheck.getUTCDate() !== day ||
    (hour !== null && (hour < 0 || hour > 23)) ||
    (minute !== null && (minute < 0 || minute > 59))
  ) return null;
  return {
    year,
    month,
    day,
    hour,
    minute,
    dayValue,
    minuteValue: hour === null || minute === null
      ? null
      : dayValue * 1_440 + hour * 60 + minute,
  };
}

function chineseDayCount(value: number): string {
  const digits = ["零", "一", "两", "三", "四", "五", "六", "七", "八", "九"];
  if (value < 10) return digits[value] ?? String(value);
  if (value === 10) return "十";
  if (value < 20) return `十${digits[value - 10] ?? ""}`;
  if (value < 30) return `二十${value === 20 ? "" : digits[value - 20] ?? ""}`;
  if (value === 30) return "三十";
  return String(value);
}

function clockText(moment: GregorianMoment): string {
  if (moment.hour === null || moment.minute === null) return "";
  return `${String(moment.hour).padStart(2, "0")}:${String(moment.minute).padStart(2, "0")}`;
}

function sameDayTime(moment: GregorianMoment): string {
  const clock = clockText(moment);
  if (!clock || moment.hour === null) return "当日";
  if (moment.hour < 5) return `当日凌晨${clock}`;
  if (moment.hour < 8) return `当日清晨${clock}`;
  if (moment.hour < 12) return `当日上午${clock}`;
  if (moment.hour < 14) return `当日中午${clock}`;
  if (moment.hour < 18) return `当日下午${clock}`;
  if (moment.hour < 20) return `当日傍晚${clock}`;
  return `当晚${clock}`;
}

function progressiveTime(
  value: string | undefined,
  previousValue: string | undefined,
  index: number,
): string {
  const full = naturalTime(value);
  if (index <= 0 || !previousValue?.trim()) return full;
  const current = parseGregorianMoment(value);
  const previous = parseGregorianMoment(previousValue);
  if (!current || !previous || current.year !== previous.year) return full;
  const dayDelta = current.dayValue - previous.dayValue;
  if (dayDelta < 0 || dayDelta > 30) return full;
  if (
    dayDelta === 0 &&
    current.minuteValue !== null &&
    previous.minuteValue !== null &&
    current.minuteValue <= previous.minuteValue
  ) return full;
  if (dayDelta === 0) return sameDayTime(current);
  const exactDate = `${current.month}月${current.day}日${clockText(current)}`;
  if (dayDelta === 1) return `次日，${exactDate}`;
  return `${chineseDayCount(dayDelta)}天后，${exactDate}`;
}

function naturalLocation(value: string | undefined): string {
  const parts = coordinateParts(value);
  if (parts.length < 2) return (value ?? "").trim();
  if (parts.length === 2) {
    return parts[0] === "中国" || parts[0]!.endsWith("国")
      ? parts.join("")
      : `${parts[0]}的${parts[1]}`;
  }
  const startsWithCountry = parts[0] === "中国" || parts[0]!.endsWith("国");
  return startsWithCountry
    ? `${parts[0]}${parts[1]}，${parts.slice(2).join("的")}`
    : `${parts[0]}，${parts.slice(1).join("的")}`;
}

function progressiveLocation(
  value: string | undefined,
  previousValue: string | undefined,
  index: number,
): string {
  const full = naturalLocation(value);
  if (index <= 0 || !previousValue?.trim()) return full;
  const current = coordinateParts(value);
  const previous = coordinateParts(previousValue);
  if (current.length < 2 || previous.length < 2) return full;
  let shared = 0;
  while (
    shared < current.length &&
    shared < previous.length &&
    current[shared] === previous[shared]
  ) shared += 1;
  if (shared < Math.min(2, current.length, previous.length)) return full;
  return naturalLocation(current.slice(-2).join(" · "));
}

function withoutRepeatedTime(body: string, rawTime: string | undefined, time: string): string {
  let narrative = body.trim();
  for (const candidate of new Set([(rawTime ?? "").trim(), time.trim()])) {
    if (!candidate || !narrative.startsWith(candidate)) continue;
    narrative = narrative.slice(candidate.length).replace(/^[\s，,：:、]+/u, "");
    break;
  }
  return narrative;
}

export function storyPreludeNarrative(
  beat: StoryPublicPreludeBeat,
  context: StoryPreludeNarrativeContext = {},
): string {
  const index = context.index ?? 0;
  const time = progressiveTime(beat.timeLabel, context.previousBeat?.timeLabel, index);
  const location = progressiveLocation(beat.locationLabel, context.previousBeat?.locationLabel, index);
  const body = withoutRepeatedTime(beat.body, beat.timeLabel, time);
  const coordinate = [time, location].filter(Boolean).join("，");
  if (!coordinate) return body;
  if (!body) return `${coordinate}。`;
  return `${coordinate}。${body}`;
}
